import React, { useState, useEffect, useContext, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { track } from '@lf/analytics-events';
import { getSubscriptionAction } from '../api/lfApiSubscription';
import { getChildProfiles, addChildProfile } from '../lib/parent/parentApi';
import { readPendingShareToken } from '../lib/share/pendingShare';
import SubscriptionActionMessage from '../cards/parent/SubscriptionActionMessage';
import ChildProfiles from '../cards/parent/ChildProfiles';
import ChildProfile from '../cards/parent/ChildProfile';
import AddChildProfile from '../cards/parent/ChildProfileAdd';
import Settings from '../cards/parent/Settings';
import PendingChildLinks from '../cards/parent/PendingChildLinks';
import { HeaderContext } from '../contexts/HeaderContext';
import { useUser } from '../contexts/UserContext';

const ParentPage = () => {
    const {
        userProfile,
        setUserProfile,
        subscriptionAction,
        setSubscriptionAction,
        logout: logoutFunc,
    } = useUser();
    const [isLoadingChildProfiles, setIsLoadingChildProfiles] = useState(true);
    const [childProfiles, setChildProfiles] = useState([]);

    const { setHeaderData } = useContext(HeaderContext);

    const navigate = useNavigate();
    const location = useLocation();
    // Track which child the URL points at so we can fire `child_switched`
    // when the parent picks a different child bubble in the header.
    const previousChildIdRef = useRef(null);
    // Fire `dashboard_viewed` once per child-context. The parent
    // dashboard mounts once and the child bubble changes the URL but
    // not the route; this effect catches both.
    useEffect(() => {
        const match = location.pathname.match(/^\/profile\/([^/]+)/);
        const username = match ? match[1] : null;
        if (username) {
            const child = childProfiles.find((c) => c.username === username);
            const childId = child?.id || username;
            const prev = previousChildIdRef.current;
            if (prev && prev !== childId) {
                track('child_switched', { from_child_id: prev, to_child_id: childId });
            }
            track('dashboard_viewed', { child_id: childId });
            previousChildIdRef.current = childId;
        } else if (location.pathname === '' || location.pathname === '/') {
            track('dashboard_viewed', {});
        }
        // childProfiles intentionally excluded: we don't want to refire
        // when the list is hydrated; the URL is the source of truth.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        if (location.pathname.startsWith('/setting')) {
            track('settings_opened', { section: 'profile' });
        }
    }, [location.pathname]);

    const navigateToParent = () => {
        navigate('');
    };

    const navigateToChildProfile = (username) => {
        navigate(`profile/${username}`);
    };

    const navigateToAddChildProfile = () => {
        navigate('add_profile');
    };

    const logout = () => {
        setChildProfiles([]);
        setHeaderData([]);

        logoutFunc();
    };

    const fetchChildProfiles = async () => {
        const profiles = await getChildProfiles();
        setChildProfiles(profiles);
        setHeaderData(profiles);

        setIsLoadingChildProfiles(false);
    };

    const fetchSubscriptionAction = async () => {
        const action = await getSubscriptionAction(false);
        if (action && action.action) {
            setSubscriptionAction(action);
        }
    };

    const addAndUpdateChildProfile = async (childProfile) => {
        const profile = await addChildProfile(childProfile);
        if (profile) {
            await fetchChildProfiles();
            await fetchSubscriptionAction();
        }
        return profile;
    };

    const onSelectChildProfile = (username) => {
        navigateToChildProfile(username);
    };

    const onAddChildProfile = () => {
        navigateToAddChildProfile();
    };

    const onAddChildProfileOk = async (childProfile) => {
        const res = await addAndUpdateChildProfile(childProfile);
        if (res) {
            const shareToken = readPendingShareToken();
            if (shareToken) {
                navigate(`/share/${shareToken}`);
            } else {
                navigateToChildProfile(res.username);
            }
        }
        else {
            navigateToParent();
        }
    };

    const onCancel = () => {
        navigateToParent();
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchChildProfiles();
            await fetchSubscriptionAction();
        };
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When a parent lands with a pending share but no children yet, route
    // them through the add-child flow so the share import has a target.
    useEffect(() => {
        if (isLoadingChildProfiles) return;
        if (childProfiles && childProfiles.length > 0) return;
        const isRoot = location.pathname === '' || location.pathname === '/';
        if (!isRoot) return;
        const shareToken = readPendingShareToken();
        if (shareToken) {
            navigate(`add_profile?share=${encodeURIComponent(shareToken)}`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingChildProfiles, childProfiles, location.pathname]);

    const addProfileShareToken = (() => {
        const params = new URLSearchParams(location.search);
        return params.get('share');
    })();

    // A parent with no active subscription hard-gates the dashboard: render only
    // the subscribe panel so they can focus on billing, with no child bubbles or
    // study plans behind it. Payment-retry states (a charge failed on an existing
    // subscription) keep grace access and surface a lighter banner above the
    // dashboard instead.
    const action = subscriptionAction?.action;
    const isLoaded = !isLoadingChildProfiles && childProfiles;
    const isPaymentRetry =
        action === 'update_payment_past_due' || action === 'update_payment_unpaid';
    const isSubscribeBlocked = isLoaded && action && action !== 'none' && !isPaymentRetry;

    if (isSubscribeBlocked) {
        return <SubscriptionActionMessage action={subscriptionAction} />;
    }

    return <div>
        {
            !isLoadingChildProfiles &&
            <PendingChildLinks onLinkResolved={fetchChildProfiles} />
        }
        {
            isLoaded && isPaymentRetry &&
            <SubscriptionActionMessage action={subscriptionAction} />
        }

        <Routes>
            <Route path="" element={
                isLoadingChildProfiles
                    ? <div>Loading...</div>
                    : (childProfiles && childProfiles.length > 0
                        ? <Navigate to={`profile/${childProfiles[0].username}`} />
                        : <ChildProfiles
                            childProfiles={childProfiles}
                            onSelectProfile={onSelectChildProfile}
                            onAddProfile={onAddChildProfile} />)
            } />
            <Route path="profile/:username/*" element={
                <ChildProfile
                    childProfiles={childProfiles}
                    fetchChildProfiles={fetchChildProfiles}
                    subscriptionAction={subscriptionAction}
                    userProfile={userProfile}
                />
            } />
            <Route path="add_profile" element={
                <>
                    {addProfileShareToken && (
                        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-md px-4 py-2 mb-3">
                            Add a child to import the shared plan.
                        </div>
                    )}
                    <AddChildProfile childProfiles={[]} showConsent={true} quantity={childProfiles.length}
                        onOk={onAddChildProfileOk} onCancel={onCancel} />
                </>
            } />
            <Route path="setting/*" element={
                <Settings userProfile={userProfile} setUserProfile={setUserProfile} logout={logout} subscriptionAction={subscriptionAction} />
            } />
        </Routes>
    </div>;
};

export default ParentPage;
